/**
 * @fileoverview Mock backend for the MFA-verify and resend endpoints.
 * @description Registers POST /identity/mfa/verify and POST /identity/mfa/resend on the feature's
 * shared mock adapter (see auth-mock-adapter.ts). Both are keyed by the challenge issued by
 * /identity/login, looked up in challenge-store.ts, matching the real backend's server-side lookup
 * instead of trusting anything the client echoes back. The user the challenge belongs to is read
 * from the shared mock user directory (mock-users.json's fixed users, plus anyone registered at
 * runtime by accepting an invitation — see mock-user-directory.ts), not mock-users.json directly,
 * so a freshly-accepted invitation can complete 2FA too. verify's success response has no
 * refresh-token cookie to set (axios-mock-adapter can't simulate one — see mock-token.utils.ts) and
 * no role/profile fields, matching the real endpoint; /identity/me is what supplies those now.
 * OTP_SCENARIO_TRIGGERS lets every non-success appCode in the verify contract be exercised on
 * demand via a reserved code, the same way auth-mock.service.ts's login scenarios work, since the
 * demo flow only ever hands out one real code (123456). resend has no equivalent user-typed field
 * to key a reserved trigger off — challengeId/clientDeviceId are both internal plumbing, never
 * something a tester enters — so only its naturally-reachable scenarios are modeled (cooldown,
 * max-resends, challenge invalid); the endpoint-rate-limit/flow-expired/service-unavailable
 * appCodes (1014-1016) have no trigger here. A no-op when VITE_ENABLE_MOCK_API is false, so
 * switching to a real backend is deleting this file's one import in features/auth/index.ts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authMockAdapter } from './auth-mock-adapter'
import {
  deleteChallenge,
  getChallenge,
  incrementFailedAttempts,
  rotateChallenge,
} from './challenge-store'
import { buildMockErrorEnvelope, buildMockSuccessEnvelope } from './mock-envelope.utils'
import { createMockAccessToken } from './mock-token.utils'
import { findMockUserById } from './mock-user-directory'
import { getCurrentSession } from './session-mock-store'

import type {
  ResendTwoFactorRequest,
  ResendTwoFactorResponse,
  SessionResponse,
  VerifyTwoFactorRequest,
} from '@/features/auth/services/two-factor.service'

const MAX_FAILED_ATTEMPTS = 5
const MAX_RESENDS = 3
const ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000
const SESSION_IDLE_TTL_MS = 30 * 60 * 1000
const SESSION_ABSOLUTE_TTL_MS = 24 * 60 * 60 * 1000

const VERIFY_CHALLENGE_INVALID_ERROR = buildMockErrorEnvelope(
  401,
  '1007',
  'MFA challenge is invalid or no longer usable.',
)
const RESEND_CHALLENGE_INVALID_ERROR = buildMockErrorEnvelope(
  401,
  '1013',
  'MFA challenge is invalid or no longer usable.',
)

/**
 * @description Reserved OTP values that let every verify-otp appCode be exercised on demand,
 * regardless of the actual pending challenge — QA test triggers, not real backend behavior.
 */
const OTP_SCENARIO_TRIGGERS: Record<string, [number, ReturnType<typeof buildMockErrorEnvelope>]> = {
  '000001': [
    403,
    buildMockErrorEnvelope(403, '1006', 'Maximum MFA verification attempts exceeded.'),
  ],
  '000002': [429, buildMockErrorEnvelope(429, '1009', 'MFA verification rate limit exceeded.')],
  '000003': [
    503,
    buildMockErrorEnvelope(
      503,
      '1010',
      'Authentication completion service temporarily unavailable.',
    ),
  ],
  '000004': [401, VERIFY_CHALLENGE_INVALID_ERROR],
  '000005': [401, buildMockErrorEnvelope(401, '1008', 'Authentication flow has expired.')],
}

if (authMockAdapter) {
  authMockAdapter.onPost('/identity/mfa/verify').reply((config) => {
    const { challengeId, otp } = JSON.parse(config.data as string) as VerifyTwoFactorRequest
    const trigger = OTP_SCENARIO_TRIGGERS[otp]

    if (trigger) {
      return trigger
    }

    const challenge = getChallenge(challengeId)

    if (!challenge || challenge.purpose === 'ACTIVATION') {
      return [401, VERIFY_CHALLENGE_INVALID_ERROR]
    }

    if (Date.now() > challenge.expiresAt) {
      return [401, buildMockErrorEnvelope(401, '1005', 'MFA challenge has expired.')]
    }

    if (otp !== challenge.code) {
      const updated = incrementFailedAttempts(challengeId)

      if (updated && updated.failedAttempts >= MAX_FAILED_ATTEMPTS) {
        deleteChallenge(challengeId)
        return [
          403,
          buildMockErrorEnvelope(403, '1006', 'Maximum MFA verification attempts exceeded.'),
        ]
      }

      return [401, buildMockErrorEnvelope(401, '1004', 'MFA Verification code is invalid')]
    }

    const match = findMockUserById(challenge.userId)

    if (!match) {
      return [401, VERIFY_CHALLENGE_INVALID_ERROR]
    }

    deleteChallenge(challengeId)

    const now = Date.now()
    const response: SessionResponse = {
      authenticationStatus: 'SUCCESS',
      accessToken: createMockAccessToken(match.id),
      tokenType: 'Bearer',
      accessTokenExpiresAt: new Date(now + ACCESS_TOKEN_TTL_MS).toISOString(),
      sessionId: getCurrentSession(match.id).sessionId,
      sessionIdleExpiresAt: new Date(now + SESSION_IDLE_TTL_MS).toISOString(),
      sessionAbsoluteExpiresAt: new Date(now + SESSION_ABSOLUTE_TTL_MS).toISOString(),
      mustChangePassword: match.mustChangePassword,
    }

    return [200, buildMockSuccessEnvelope(response, 'Authentication successful')]
  })

  authMockAdapter.onPost('/identity/mfa/resend').reply((config) => {
    const { challengeId } = JSON.parse(config.data as string) as ResendTwoFactorRequest
    const existing = getChallenge(challengeId)

    if (!existing || existing.purpose === 'ACTIVATION') {
      return [401, RESEND_CHALLENGE_INVALID_ERROR]
    }

    if (existing.resendCount >= MAX_RESENDS) {
      deleteChallenge(challengeId)
      return [403, buildMockErrorEnvelope(403, '1012', 'Maximum MFA resend attempts exceeded.')]
    }

    if (Date.now() < existing.resendAvailableAt) {
      return [429, buildMockErrorEnvelope(429, '1011', 'MFA resend cooldown has not elapsed.')]
    }

    const rotated = rotateChallenge(challengeId)

    if (!rotated) {
      return [401, RESEND_CHALLENGE_INVALID_ERROR]
    }

    const match = findMockUserById(rotated.userId)

    const response: ResendTwoFactorResponse = {
      challengeId: rotated.challengeToken,
      channel: 'EMAIL',
      destinationMasked: match ? match.email.replace(/^(.).*(@.*)$/, '$1***$2') : '',
      expiresAt: new Date(rotated.expiresAt).toISOString(),
      resendAvailableAt: new Date(rotated.resendAvailableAt).toISOString(),
    }

    return [200, buildMockSuccessEnvelope(response, 'Verification code resent successfully')]
  })
}
